import { 
  Box, 
  Button, 
  Checkbox, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  FormControlLabel, 
  Paper, 
  Stepper, 
  Step, 
  StepLabel, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Typography 
} from '@mui/material';
import { FC, useCallback, useMemo, useState } from 'react';
import { StandardDialogHeader } from '../CommonComponents/StandardDialogHeader';
import { CSVColumnDefinition } from '../../models/CSVColumnDefinition';
import { generateCSV } from '../../util/generateCSV';
import { downloadBlobFile } from '../../util/exportToCsv';

interface CSVExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: any[];
  columnDefinitions: CSVColumnDefinition<any>[];
  defaultFilename?: string;
}

const steps = ['Select Columns', 'Preview Data', 'Download'];

export const CSVExportModal: FC<CSVExportModalProps> = ({ 
  isOpen, 
  onClose, 
  data, 
  columnDefinitions, 
  defaultFilename = 'export' 
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    columnDefinitions.map(col => col.key)
  );

  const selectedColumnDefinitions = useMemo(() => {
    return columnDefinitions.filter(col => selectedColumns.includes(col.key));
  }, [columnDefinitions, selectedColumns]);

  const csvContent = useMemo(() => {
    if (selectedColumnDefinitions.length === 0) return '';
    return generateCSV(data, selectedColumnDefinitions);
  }, [data, selectedColumnDefinitions]);

  const previewData = useMemo(() => {
    if (selectedColumnDefinitions.length === 0) return [];
    return data.slice(0, 10).map(row => {
      const previewRow: Record<string, string> = {};
      selectedColumnDefinitions.forEach(col => {
        const value = col.csvFormatter 
          ? col.csvFormatter(row)
          : (row as any)[col.key];
        previewRow[col.key] = String(value || '');
      });
      return previewRow;
    });
  }, [data, selectedColumnDefinitions]);

  const handleColumnToggle = useCallback((columnKey: string) => {
    setSelectedColumns(prev => 
      prev.includes(columnKey)
        ? prev.filter(key => key !== columnKey)
        : [...prev, columnKey]
    );
  }, []);

  const handleNext = useCallback(() => {
    setActiveStep(prev => prev + 1);
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep(prev => prev - 1);
  }, []);

  const handleClose = useCallback(() => {
    setActiveStep(0);
    setSelectedColumns(columnDefinitions.map(col => col.key));
    onClose();
  }, [columnDefinitions, onClose]);

  const handleDownload = useCallback(() => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const filename = `${defaultFilename}_${new Date().toISOString().split('T')[0]}.csv`;
    downloadBlobFile(blob, filename);
    handleClose();
  }, [csvContent, defaultFilename, handleClose]);

  const canProceed = useMemo(() => selectedColumns.length > 0, [selectedColumns]);

  const renderStepContent = useCallback(() => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select columns to include in your export:
            </Typography>
            <Box sx={{ mt: 2, maxHeight: 400, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
              {columnDefinitions.map(column => (
                <FormControlLabel
                  key={column.key}
                  control={
                    <Checkbox
                      checked={selectedColumns.includes(column.key)}
                      onChange={() => handleColumnToggle(column.key)}
                    />
                  }
                  label={column.label}
                />
              ))}
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Preview of your export ({previewData.length} of {data.length} rows shown):
            </Typography>
            <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {selectedColumnDefinitions.map(column => (
                      <TableCell key={column.key}>
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.map((row, index) => (
                    <TableRow key={index}>
                      {selectedColumnDefinitions.map(column => (
                        <TableCell key={column.key}>
                          {row[column.key]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Ready to download
            </Typography>
            <Typography>
            <strong>Your CSV file will be downloaded as:</strong> {defaultFilename}_{new Date().toISOString().split('T')[0]}.csv
            </Typography>
            <Typography sx={{ mt: 1 }}>
              <strong>Total rows:</strong> {data.length}
            </Typography>
            <Typography sx={{ mt: 1 }}>
              <strong>Selected columns:</strong> {selectedColumnDefinitions.map(col => col.label).join(', ')}
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  }, [
    activeStep,
    columnDefinitions,
    selectedColumns,
    handleColumnToggle,
    previewData,
    data.length,
    selectedColumnDefinitions,
    defaultFilename
  ]);

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="lg" fullWidth>
      <StandardDialogHeader 
        icon={<></>} 
        text="Export to CSV" 
      />
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {renderStepContent()}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button 
            variant="contained" 
            onClick={handleNext}
            disabled={!canProceed}
          >
            Next
          </Button>
        ) : (
          <Button 
            variant="contained" 
            onClick={handleDownload}
            disabled={!canProceed}
            sx={{ width: '160px !important' }}
          >
            Download CSV
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
